import type { WatchHistoryEntry } from '../types/profile.js';

export const WATCHED_COMPLETION_RATIO = 0.7;

export interface VideoLike {
    id: string;
    snippet?: {
        publishedAt?: string;
        title?: string;
    };
}

type WatchProgressEntry = Pick<WatchHistoryEntry, 'videoId' | 'watchedDurationSeconds'> & {
    totalDurationSeconds?: number | null;
    watchedAt?: string;
};

export interface WatchProgress {
    watchedDurationSeconds: number;
    totalDurationSeconds: number;
    ratio: number;
    completed: boolean;
    watchedAt?: string;
}

function normalizeSeconds(value: number | null | undefined): number {
    return Number.isFinite(value) && value !== null && value !== undefined
        ? Math.max(0, Math.floor(value))
        : 0;
}

export function isWatchHistoryEntryCompleted(
    entry: Pick<WatchHistoryEntry, 'watchedDurationSeconds'> & { totalDurationSeconds?: number | null },
    completionRatio = WATCHED_COMPLETION_RATIO
): boolean {
    const watchedDurationSeconds = normalizeSeconds(entry.watchedDurationSeconds);
    const totalDurationSeconds = normalizeSeconds(entry.totalDurationSeconds);

    if (watchedDurationSeconds <= 0 || totalDurationSeconds <= 0) {
        return false;
    }

    return watchedDurationSeconds / totalDurationSeconds >= completionRatio;
}

export function getWatchProgressByVideoId(
    watchHistory: WatchProgressEntry[],
    completionRatio = WATCHED_COMPLETION_RATIO
): Map<string, WatchProgress> {
    const progressByVideoId = new Map<string, WatchProgress>();

    for (const entry of watchHistory) {
        const watchedDurationSeconds = normalizeSeconds(entry.watchedDurationSeconds);
        const totalDurationSeconds = normalizeSeconds(entry.totalDurationSeconds);
        const existing = progressByVideoId.get(entry.videoId);

        const nextWatchedDurationSeconds = Math.max(existing?.watchedDurationSeconds || 0, watchedDurationSeconds);
        const nextTotalDurationSeconds = Math.max(existing?.totalDurationSeconds || 0, totalDurationSeconds);
        const nextWatchedAt = existing?.watchedAt && entry.watchedAt
            ? (Date.parse(entry.watchedAt) > Date.parse(existing.watchedAt) ? entry.watchedAt : existing.watchedAt)
            : entry.watchedAt || existing?.watchedAt;
        const ratio = nextTotalDurationSeconds > 0
            ? Math.min(1, nextWatchedDurationSeconds / nextTotalDurationSeconds)
            : 0;

        progressByVideoId.set(entry.videoId, {
            watchedDurationSeconds: nextWatchedDurationSeconds,
            totalDurationSeconds: nextTotalDurationSeconds,
            ratio,
            completed: nextTotalDurationSeconds > 0 && ratio >= completionRatio,
            watchedAt: nextWatchedAt
        });
    }

    return progressByVideoId;
}

export function getWatchedVideoIds(watchHistory: WatchProgressEntry[]): Set<string> {
    const completedVideoIds = new Set<string>();
    const progressByVideoId = getWatchProgressByVideoId(watchHistory);

    for (const [videoId, progress] of progressByVideoId) {
        if (progress.completed) {
            completedVideoIds.add(videoId);
        }
    }

    return completedVideoIds;
}

export function orderVideosByWatchStatus<T extends VideoLike>(
    videos: readonly T[],
    watchedVideoIds: Iterable<string>
): T[] {
    const watched = new Set(watchedVideoIds);
    const freshVideos: T[] = [];
    const watchedVideos: T[] = [];

    for (const video of videos) {
        if (watched.has(video.id)) {
            watchedVideos.push(video);
        } else {
            freshVideos.push(video);
        }
    }

    return [...freshVideos, ...watchedVideos];
}

function getPublishedAtTime(video: VideoLike): number {
    const publishedAt = video.snippet?.publishedAt;
    if (!publishedAt) {
        return 0;
    }

    const timestamp = Date.parse(publishedAt);
    return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function orderVideosByStableFreshness<T extends VideoLike>(videos: readonly T[]): T[] {
    return [...videos].sort((a, b) => {
        const publishedAtDifference = getPublishedAtTime(b) - getPublishedAtTime(a);
        if (publishedAtDifference !== 0) {
            return publishedAtDifference;
        }

        const titleDifference = (a.snippet?.title ?? '').localeCompare(b.snippet?.title ?? '');
        if (titleDifference !== 0) {
            return titleDifference;
        }

        return a.id.localeCompare(b.id);
    });
}

export function orderVideosByFreshnessThenWatchStatus<T extends VideoLike>(
    videos: readonly T[],
    watchedVideoIds: Iterable<string>
): T[] {
    return orderVideosByWatchStatus(orderVideosByStableFreshness(videos), watchedVideoIds);
}
